import { useState, useEffect, useCallback } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import { plural } from '@lingui/core/macro';
import { translateGroupName } from '@pantry/i18n';
import { Card, CardContent, Button, cn } from '@pantry/ui';
import { type Household, type ConsolidatedItem } from '@pantry/shared';
import { ArchiveBoxIcon, ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { ItemCard } from './item-card';

/**
 * Consolidated item card with split navigation zones.
 *
 * Left zone (icon + info): navigates to detail view.
 * Right zone (75px, chevron): expands/collapses inline items.
 */
export function ConsolidatedItemCard({
  consolidated,
  isExpanded,
  onToggle,
  onNavigate,
  householdId,
  household,
  onSetTarget,
  onAddToGroup,
}: {
  consolidated: ConsolidatedItem;
  isExpanded: boolean;
  onToggle: () => void;
  onNavigate?: () => void;
  householdId: string;
  household: Household | null;
  onSetTarget?: (consolidated: ConsolidatedItem) => void;
  onAddToGroup?: (consolidated: ConsolidatedItem) => void;
}) {
  const { t, i18n } = useLingui();

  const isGroup = !!consolidated.groupId;
  const displayName = isGroup
    ? translateGroupName(i18n, consolidated.displayName) || t`Unnamed Group`
    : consolidated.displayName || t`Unnamed Item`;
  const hasTarget = consolidated.targetQuantity != null && consolidated.targetQuantity > 0;
  const unit = consolidated.targetUnit ?? '';

  // Build subtitle based on item type
  let subtitle: string;
  if (isGroup) {
    subtitle = hasTarget
      ? t`${plural(consolidated.productCount, { one: '# product', other: '# products' })} · ${consolidated.totalActiveQuantity} ${unit} / ${consolidated.targetQuantity} ${unit}`
      : t`${plural(consolidated.productCount, { one: '# product', other: '# products' })} · ${consolidated.totalActiveQuantity} ${unit}`;
  } else {
    subtitle = hasTarget
      ? t`${consolidated.totalActiveQuantity} / ${consolidated.targetQuantity} ${unit}`
      : t`${consolidated.totalActiveQuantity} ${unit}`;
  }

  // Status badge: only when below target or depleted with a target
  const showLowStock =
    hasTarget && consolidated.isBelowTarget && consolidated.totalActiveQuantity > 0;
  const showOutOfStock = hasTarget && consolidated.totalActiveQuantity === 0;

  // Context menu for right-click actions
  const showContextAddToGroup = !isGroup && consolidated.productId && onAddToGroup;
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);

  const handleContextMenu = useCallback(
    (e: React.MouseEvent) => {
      if (!showContextAddToGroup) return;
      e.preventDefault();
      setContextMenu({ x: e.clientX, y: e.clientY });
    },
    [showContextAddToGroup]
  );

  // Close context menu on click outside or scroll
  useEffect(() => {
    if (!contextMenu) return;
    const close = () => setContextMenu(null);
    window.addEventListener('click', close);
    window.addEventListener('scroll', close, true);
    return () => {
      window.removeEventListener('click', close);
      window.removeEventListener('scroll', close, true);
    };
  }, [contextMenu]);

  return (
    <Card
      className={cn(
        'overflow-hidden relative',
        consolidated.isDepleted && 'opacity-60',
        consolidated.isBelowTarget && 'border-expiring-soon/30'
      )}
      onContextMenu={handleContextMenu}
    >
      <div className="flex w-full">
        {/* Left zone — navigates to detail view */}
        <button
          onClick={onNavigate}
          className="flex flex-1 items-center gap-4 p-4 text-left hover:bg-muted/30 transition-colors border-r border-border/50 min-w-0"
        >
          {/* Icon / Image with arrow overlay */}
          <div className="relative flex-shrink-0">
            <div className="flex h-14 w-14 items-center justify-center rounded-xl overflow-hidden bg-muted">
              {consolidated.displayIcon ? (
                <span className="text-2xl">{consolidated.displayIcon}</span>
              ) : consolidated.displayImage ? (
                <img
                  src={consolidated.displayImage}
                  alt={displayName}
                  className="h-full w-full object-cover"
                />
              ) : (
                <ArchiveBoxIcon className="h-7 w-7 text-muted-foreground" />
              )}
            </div>
            {/* Arrow navigation badge */}
            <span className="absolute -bottom-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-primary/80 text-[10px] text-primary-foreground">
              →
            </span>
          </div>

          {/* Text info */}
          <div className="flex-1 min-w-0">
            <h3 className="font-medium text-foreground truncate">{displayName}</h3>
            {consolidated.displayBrand && (
              <p className="text-xs text-muted-foreground truncate">{consolidated.displayBrand}</p>
            )}
            <div className="flex items-center gap-2 mt-1 min-w-0">
              <p className="text-xs text-muted-foreground truncate">{subtitle}</p>
              {showLowStock && (
                <span className="inline-flex items-center rounded-md bg-expiring-soon/10 px-1.5 py-0.5 text-xs font-medium text-expiring-soon whitespace-nowrap flex-shrink-0">
                  <Trans context="stock_status">low stock</Trans>
                </span>
              )}
              {showOutOfStock && (
                <span className="inline-flex items-center rounded-md bg-destructive/10 px-1.5 py-0.5 text-xs font-medium text-destructive whitespace-nowrap flex-shrink-0">
                  <Trans context="stock_status">out of stock</Trans>
                </span>
              )}
            </div>
          </div>
        </button>

        {/* Right zone — expands/collapses */}
        <button
          onClick={onToggle}
          className="flex items-center justify-center hover:bg-muted/30 transition-colors"
          style={{ minWidth: 75 }}
        >
          <div
            className={cn(
              'flex h-8 w-8 items-center justify-center rounded-lg flex-shrink-0 transition-colors',
              isExpanded ? 'bg-muted' : 'bg-transparent'
            )}
          >
            {isExpanded ? (
              <ChevronDownIcon className="h-5 w-5 text-muted-foreground" />
            ) : (
              <ChevronRightIcon className="h-5 w-5 text-muted-foreground" />
            )}
          </div>
        </button>
      </div>

      {/* Action buttons */}
      {consolidated.isDepleted && !hasTarget && onSetTarget && (
        <div className="px-3 pb-2">
          <Button
            variant="ghost"
            size="sm"
            className="text-xs text-primary"
            onClick={(e) => {
              e.stopPropagation();
              onSetTarget(consolidated);
            }}
          >
            <Trans context="action_button">Set Target</Trans>
          </Button>
        </div>
      )}
      {/* Expanded view showing individual items */}
      {isExpanded && (
        <CardContent className="pt-0 pb-4 border-t border-border/50">
          <div className="space-y-2 mt-3 max-h-[400px] overflow-y-auto">
            {consolidated.items
              .filter((item) => item.activeQuantity > 0)
              .map((item) => (
                <ItemCard
                  key={item.id}
                  item={item}
                  householdId={householdId}
                  household={household}
                  compact
                />
              ))}
          </div>
          {!isGroup && consolidated.productId && onAddToGroup && (
            <Button
              variant="ghost"
              size="sm"
              className="text-xs mt-2 w-full"
              onClick={(e) => {
                e.stopPropagation();
                onAddToGroup(consolidated);
              }}
            >
              <Trans context="action_button">Add to Group</Trans>
            </Button>
          )}
        </CardContent>
      )}

      {/* Right-click context menu */}
      {contextMenu && showContextAddToGroup && (
        <div
          className="fixed z-50 min-w-[160px] rounded-md border bg-popover p-1 shadow-md"
          style={{ left: contextMenu.x, top: contextMenu.y }}
        >
          <button
            className="flex w-full items-center rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground transition-colors"
            onClick={() => {
              onAddToGroup!(consolidated);
              setContextMenu(null);
            }}
          >
            <Trans context="action_button">Add to Group</Trans>
          </button>
        </div>
      )}
    </Card>
  );
}
